import React from "react";
import { Box, Container, Typography } from "@mui/material";
import {
  AutoAwesomeOutlined,
  DashboardCustomizeOutlined,
  MicNoneOutlined,
  SpaceDashboardOutlined,
} from "@mui/icons-material";
import MarketingSiteHeader from "./MarketingSiteHeader";
import MarketingSiteFooter from "./MarketingSiteFooter";
import { InkyAvatar, InkyMascot } from "./brand";
import { IconTile } from "./pagekit";

/** Public route of the release notes page (works signed in and signed out). */
export const WHATS_NEW_PATH = "/whats-new";

/**
 * Props for links that open the release notes in a new tab, so the user keeps their place in
 * the app. Spread onto an anchor-like component (`component="a"`).
 */
export const WHATS_NEW_LINK_PROPS = Object.freeze({ href: WHATS_NEW_PATH, target: "_blank", rel: "noopener" });

/** Page title. */
export const WHATS_NEW_TITLE = "What's new in Quill";

/**
 * Highlights of the current release, in display order.
 * @type {ReadonlyArray<{id: string, text: string}>}
 */
export const WHATS_NEW_ITEMS = Object.freeze([
  {
    id: "inky",
    text: "Meet Inky, your new AI teammate. Inky drafts and edits your documents, and you review every change before it lands.",
  },
  { id: "models", text: "Smarter AI on every plan, with our most capable model on Pro and Pro Plus." },
  { id: "templates", text: "More consistent results from your templates." },
  { id: "workspace", text: "A cleaner, faster workspace with a movable, resizable editor." },
]);

/** Upcoming feature teaser. */
export const WHATS_NEW_COMING_SOON = Object.freeze({
  lead: "Talk to Quill.",
  rest: "Speak your updates and Inky turns them into text.",
});

/**
 * Icon for a highlight row.
 * @param {string} id - Highlight id.
 * @returns {JSX.Element} Icon tile.
 */
function HighlightIcon({ id }) {
  if (id === "inky") return <InkyAvatar size={36} mood="happy" decorative />;
  const icons = {
    models: <AutoAwesomeOutlined />,
    templates: <DashboardCustomizeOutlined />,
    workspace: <SpaceDashboardOutlined />,
  };
  return <IconTile tone="accent">{icons[id] || <AutoAwesomeOutlined />}</IconTile>;
}

const surfaceSx = {
  bgcolor: "var(--q-surface, #FFFFFF)",
  border: "1px solid var(--q-border, #E3E0DA)",
  borderRadius: "var(--q-radius-xl, 12px)",
  boxShadow: "var(--q-shadow-sm, none)",
};

/**
 * Public "What's new" release notes page, styled like the marketing site. Linked from the Home
 * header, Settings, the sidebar account menu and the command palette (opened in a new tab).
 * @returns {JSX.Element} Page.
 */
export default function WhatsNewPage() {
  return (
    <Box
      sx={{
        bgcolor: "var(--q-bg, #F6F5F2)",
        color: "var(--q-text, #1C1B19)",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <MarketingSiteHeader />

      <Box component="main" sx={{ flex: 1, py: { xs: 5, md: 8 } }}>
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box
            component="header"
            sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2 }, mb: { xs: 3, md: 4 } }}
          >
            <Box sx={{ flex: "none", display: "flex" }}>
              <InkyMascot size={52} mood="happy" decorative />
            </Box>
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: 28, sm: 36 },
                lineHeight: 1.15,
                fontWeight: 650,
                letterSpacing: "-0.02em",
                overflowWrap: "anywhere",
              }}
            >
              {WHATS_NEW_TITLE}
            </Typography>
          </Box>

          <Box component="section" aria-label="Highlights" sx={{ ...surfaceSx, overflow: "hidden" }}>
            <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
              {WHATS_NEW_ITEMS.map((item, index) => (
                <Box
                  component="li"
                  key={item.id}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: { xs: 1.5, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    py: { xs: 2, sm: 2.5 },
                    borderTop: index === 0 ? "none" : "1px solid var(--q-row-divider, #F0EDE8)",
                  }}
                >
                  <HighlightIcon id={item.id} />
                  <Typography
                    sx={{ fontSize: { xs: 15, sm: 16 }, lineHeight: 1.6, color: "var(--q-text-2, #3D3A35)", pt: 0.5 }}
                  >
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            component="section"
            aria-labelledby="whats-new-coming-soon"
            sx={{ ...surfaceSx, mt: { xs: 3, md: 4 }, px: { xs: 2, sm: 3 }, py: { xs: 2.5, sm: 3 } }}
          >
            <Typography
              id="whats-new-coming-soon"
              component="h2"
              sx={{
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "var(--q-muted, #5E5A54)",
                mb: 1.5,
              }}
            >
              Coming soon
            </Typography>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: { xs: 1.5, sm: 2 } }}>
              <IconTile tone="neutral">
                <MicNoneOutlined />
              </IconTile>
              <Typography sx={{ fontSize: { xs: 15, sm: 16 }, lineHeight: 1.6, color: "var(--q-text-2, #3D3A35)", pt: 0.5 }}>
                <Box component="strong" sx={{ fontWeight: 600, color: "var(--q-text, #1C1B19)" }}>
                  {WHATS_NEW_COMING_SOON.lead}
                </Box>{" "}
                {WHATS_NEW_COMING_SOON.rest}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <MarketingSiteFooter />
    </Box>
  );
}
